import { type FC, type ReactNode, useEffect, useRef, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { setAccessToken, signOutUser } from 'redux/slices/user.slice';
import type { RootState } from 'redux/store';
import { PATHS_WITH_SPECIAL_AUTH, refreshAccessToken } from 'services/api/baseApi';
import { clearPersistedUser } from 'services/api/clearPersistUser';

import { isAccessTokenExpired } from '../../authStorage';

interface Props {
  children: ReactNode;
}

/**
 * On a hard reload, redux-persist rehydrates the user — including a possibly-expired 15m
 * access token — before the first render. The route guard (routes/authRoute.tsx) then
 * checks the token's `exp` synchronously and redirects to /sign-in when it is expired,
 * even though a live refresh-token cookie could still mint a fresh access token. That
 * synchronous redirect races ahead of the in-app silent refresh in services/api/baseApi.ts
 * (which only runs in response to an XHR 401), so reloading near the 15-minute mark logged
 * the user out.
 *
 * This gate closes the race: before the router renders, if the persisted access token is
 * expired it performs ONE silent /auth/refresh (shared single-flight with baseApi). On
 * success the new token is stored and the app boots signed-in; only a dead refresh session
 * falls through to the normal sign-in redirect. A still-valid token (the common case) skips
 * the gate entirely with no added wait — only an expired-token reload pays the round-trip.
 */
const AuthBootstrapGate: FC<Props> = ({ children }) => {
  const dispatch = useDispatch();
  const accessToken = useSelector((state: RootState) => state.user?.accessToken ?? '');
  // Decided once, off the token present at mount. Later setAccessToken updates must not
  // re-open the gate, so keep the decision in a ref rather than recomputing on render.
  // Skip the embedded -demo/-client paths entirely: they receive their token via
  // postMessage (no /auth refresh cookie), so refreshing there is a pointless cross-site
  // round-trip that would only signOut — mirrors the exclusion in baseApi.ts.
  const shouldRefreshOnMount = useRef(
    !PATHS_WITH_SPECIAL_AUTH.includes(window.location.pathname) && isAccessTokenExpired(accessToken)
  );
  // The mount-time (expired) token, captured so the effect doesn't depend on the reactive
  // value and so a sibling tab's already-rotated token can be adopted instead of refreshing.
  const staleTokenRef = useRef(accessToken);
  const [ready, setReady] = useState(!shouldRefreshOnMount.current);

  useEffect(() => {
    if (!shouldRefreshOnMount.current) {
      return;
    }
    let cancelled = false;
    void (async () => {
      const newToken = await refreshAccessToken(staleTokenRef.current);
      if (cancelled) {
        return;
      }
      if (newToken) {
        dispatch(setAccessToken(newToken));
      } else {
        dispatch(signOutUser());
        clearPersistedUser();
      }
      setReady(true);
    })();
    return () => {
      cancelled = true;
    };
  }, [dispatch]);

  if (!ready) {
    // Render nothing while the one-shot refresh resolves — matches the PersistGate's
    // `loading={null}` and, crucially, keeps the route guard from redirecting in the
    // meantime. The window is a single /auth/refresh round-trip on expired-token reloads.
    return null;
  }

  return <>{children}</>;
};

export default AuthBootstrapGate;
